// Notification Service
// This service handles sending notifications through various channels

export interface EmailNotification {
  to: string;
  subject: string;
  html: string;
  text?: string;
}

export interface PushNotification {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: any;
}

export class NotificationService {
  private static instance: NotificationService;
  private emailServiceUrl: string | null = null;

  private constructor() {
    // Initialize with environment variables
    this.emailServiceUrl = process.env.REACT_APP_EMAIL_SERVICE_URL || null;
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  /**
   * Send email notification
   * This integrates with your email service (SendGrid, AWS SES, etc.)
   */
  async sendEmail(notification: EmailNotification): Promise<boolean> {
    try {
      // If you have an email service endpoint, use it
      if (this.emailServiceUrl) {
        const response = await fetch(this.emailServiceUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(notification),
        });

        if (!response.ok) {
          throw new Error(`Email service responded with ${response.status}`);
        }

        return true;
      }

      // Fallback: Log the email (for development)
      console.log('📧 Email notification would be sent:', {
        to: notification.to,
        subject: notification.subject,
        html: notification.html,
        text: notification.text,
      });

      // In production, you would integrate with:
      // - SendGrid
      // - AWS SES
      // - Mailgun
      // - Nodemailer
      // - Or your own email service

      return true;
    } catch (error) {
      console.error('Failed to send email notification:', error);
      return false;
    }
  }

  /**
   * Send browser push notification
   */
  async sendPushNotification(notification: PushNotification): Promise<boolean> {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.body,
          icon: notification.icon || '/favicon.ico',
          badge: notification.badge || '/favicon.ico',
          tag: notification.tag,
          data: notification.data,
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to send push notification:', error);
      return false;
    }
  }

  /**
   * Send SMS notification (if you have an SMS service)
   */
  async sendSMS(to: string, message: string): Promise<boolean> {
    try {
      // Integrate with SMS services like:
      // - Twilio
      // - AWS SNS
      // - MessageBird
      // - Or your own SMS gateway

      console.log('📱 SMS notification would be sent:', { to, message });
      return true;
    } catch (error) {
      console.error('Failed to send SMS notification:', error);
      return false;
    }
  }

  /**
   * Send Slack notification (if you have Slack integration)
   */
  async sendSlack(channel: string, message: string, attachments?: any[]): Promise<boolean> {
    try {
      // Integrate with Slack webhooks or Slack API
      console.log('💬 Slack notification would be sent:', { channel, message, attachments });
      return true;
    } catch (error) {
      console.error('Failed to send Slack notification:', error);
      return false;
    }
  }

  /**
   * Send Discord notification (if you have Discord integration)
   */
  async sendDiscord(webhookUrl: string, message: string, embeds?: any[]): Promise<boolean> {
    try {
      // Integrate with Discord webhooks
      console.log('🎮 Discord notification would be sent:', { webhookUrl, message, embeds });
      return true;
    } catch (error) {
      console.error('Failed to send Discord notification:', error);
      return false;
    }
  }

  /**
   * Send notification through multiple channels
   */
  async sendMultiChannel(notification: {
    email?: EmailNotification;
    push?: PushNotification;
    sms?: { to: string; message: string };
    slack?: { channel: string; message: string; attachments?: any[] };
    discord?: { webhookUrl: string; message: string; embeds?: any[] };
  }): Promise<{
    email: boolean;
    push: boolean;
    sms: boolean;
    slack: boolean;
    discord: boolean;
  }> {
    const results = {
      email: false,
      push: false,
      sms: false,
      slack: false,
      discord: false,
    };

    // Send email notification
    if (notification.email) {
      results.email = await this.sendEmail(notification.email);
    }

    // Send push notification
    if (notification.push) {
      results.push = await this.sendPushNotification(notification.push);
    }

    // Send SMS notification
    if (notification.sms) {
      results.sms = await this.sendSMS(notification.sms.to, notification.sms.message);
    }

    // Send Slack notification
    if (notification.slack) {
      results.slack = await this.sendSlack(
        notification.slack.channel,
        notification.slack.message,
        notification.slack.attachments
      );
    }

    // Send Discord notification
    if (notification.discord) {
      results.discord = await this.sendDiscord(
        notification.discord.webhookUrl,
        notification.discord.message,
        notification.discord.embeds
      );
    }

    return results;
  }

  /**
   * Generate HTML email template for message notifications
   */
  generateMessageEmailTemplate(brandName: string, requestType: string, message: string, requestId: string): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New Message from ${brandName}</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: #3B82F6; color: white; padding: 20px; text-align: center; border-radius: 8px 8px 0 0; }
          .content { background: #f9fafb; padding: 20px; border-radius: 0 0 8px 8px; }
          .button { display: inline-block; background: #3B82F6; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; margin: 20px 0; }
          .footer { text-align: center; margin-top: 30px; color: #6b7280; font-size: 14px; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>🔔 New Message</h1>
            <p>You have a new message from ${brandName}</p>
          </div>
          <div class="content">
            <h2>Request: ${requestType}</h2>
            <p><strong>Message:</strong></p>
            <blockquote style="background: white; padding: 15px; border-left: 4px solid #3B82F6; margin: 20px 0;">
              ${message}
            </blockquote>
            <a href="${window.location.origin}/dashboard/requests/${requestId}" class="button">
              View Request Details
            </a>
            <p style="margin-top: 20px; color: #6b7280;">
              This is an automated notification. Please do not reply to this email.
            </p>
          </div>
          <div class="footer">
            <p>© ${new Date().getFullYear()} Code Splash. All rights reserved.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }

  /**
   * Generate plain text email for message notifications
   */
  generateMessageEmailText(brandName: string, requestType: string, message: string, requestId: string): string {
    return `
New Message from ${brandName}

Request: ${requestType}

Message:
${message}

View Request Details: ${window.location.origin}/dashboard/requests/${requestId}

This is an automated notification. Please do not reply to this email.

© ${new Date().getFullYear()} Code Splash. All rights reserved.
    `.trim();
  }
}

// Export singleton instance
export const notificationService = NotificationService.getInstance(); 